import { useEffect, useState } from 'react';
import cx from 'classnames';
import { bool, func, string } from 'prop-types';
import useSubscriptionPlanPrices from '../../hooks/useSubscriptionPlanPrices';
import { Plans } from '../constants';

const OptionButtons = ({
  handleChange = () => null,
  selected = Plans.SEASONAL,
  getCouponContentKey = (_, defaultValue) => defaultValue,
  defaultSeasonalTitle = '',
  defaultSeasonalPrice = '',
  defaultSeasonalSubText = '',
  defaultAnnualTitle = '',
  defaultAnnualPrice = '',
  defaultAnnualSubText = '',
  strikethroughText = true,
  boldAnnualSubText = true,
}) => {
  const { seasonalPrice, annualPrice } = useSubscriptionPlanPrices();
  const seasonalTitle = defaultSeasonalTitle || 'SEASONAL';
  const seasonalSubText = defaultSeasonalSubText || 'Standard';
  const annualTitle = defaultAnnualTitle || 'ANNUAL';
  const [annualSubText, setAnnualSubText] = useState('');
  const [_, setPrioritizedSeasonalPrice] = useState(null);
  const [prioritizedAnnualPrice, setPrioritizedAnnualPrice] = useState(null);

  useEffect(() => {
    setPrioritizedSeasonalPrice(defaultSeasonalPrice || `$${seasonalPrice} USD`);
    setPrioritizedAnnualPrice(defaultAnnualPrice || `$${annualPrice} USD`);
    setAnnualSubText(defaultAnnualSubText || `$${annualPrice} USD!`);
  }, [defaultSeasonalPrice, defaultAnnualPrice, defaultAnnualSubText, seasonalPrice, annualPrice]);

  const displayStrikeThroughText = () => {
    if (defaultAnnualPrice || annualPrice) {
      return prioritizedAnnualPrice !== annualSubText.substring(0, annualSubText.length - 1);
    }
    return false;
  };

  return (
    <div data-testid="ob-container" className="trialUpgrade__buttons">
      <div
        data-testid="ob-seasonal-box"
        className={cx('trialUpgrade__buttons__box', { active: selected === Plans.SEASONAL })}
        role="button"
        tabIndex="0"
        onClick={() => handleChange(Plans.SEASONAL)}
        onKeyDown={() => handleChange(Plans.SEASONAL)}
      >
        {selected === Plans.SEASONAL && (
          <div data-testid="ob-active-check" className="activeCheck">
            <i className="fas fa-check-circle" />
          </div>
        )}
        <div data-testid="ob-seasonal-title" className="trialUpgrade__buttons__title">
          {getCouponContentKey('seasonalPlan.title', seasonalTitle)}
        </div>
        {seasonalPrice && (
          <div data-testid="ob-seasonal-price" className="trialUpgrade__buttons__price">
            {`$${seasonalPrice} USD`}
          </div>
        )}
        <div data-testid="ob-seasonal-button-type" className="trialUpgrade__buttons__type">
          {getCouponContentKey('seasonalPlan.note', seasonalSubText)}
        </div>
      </div>
      <div
        data-testid="ob-annual-box"
        className={cx('trialUpgrade__buttons__box', { active: selected === Plans.ANNUAL })}
        role="button"
        tabIndex="0"
        onClick={() => handleChange(Plans.ANNUAL)}
        onKeyDown={() => handleChange(Plans.ANNUAL)}
      >
        {selected === Plans.ANNUAL && (
          <div className="activeCheck">
            <i className="fas fa-check-circle" />
          </div>
        )}
        <div data-testid="ob-annual-title" className="trialUpgrade__buttons__title">
          {getCouponContentKey('annualPlan.title', annualTitle)}
        </div>
        {displayStrikeThroughText() && (
          <div className={cx('trialUpgrade__buttons__price', { through: strikethroughText })}>
            {getCouponContentKey('annualPlan.subtitle', prioritizedAnnualPrice)}
          </div>
        )}
        {(defaultAnnualSubText || annualPrice) && (
          <div
            data-testid="ob-annual-sub-price"
            className={cx('spaced', {
              trialUpgrade__buttons__price: boldAnnualSubText,
              trialUpgrade__buttons__type: !boldAnnualSubText,
            })}
          >
            {`$${annualPrice} USD!`}
          </div>
        )}
      </div>
    </div>
  );
};

OptionButtons.propTypes = {
  handleChange: func,
  selected: string,
  getCouponContentKey: func,
  defaultSeasonalTitle: string,
  defaultSeasonalPrice: string,
  defaultSeasonalSubText: string,
  defaultAnnualTitle: string,
  defaultAnnualPrice: string,
  defaultAnnualSubText: string,
  strikethroughText: bool,
  boldAnnualSubText: bool,
};

export default OptionButtons;
